import { IReactionDisposer, autorun, reaction } from "mobx";
import { ApplicationStore } from "../../../../store/ApplicationStore";
import { CompositionStateStore } from "../../../../store/CompositionStateStore";
import { TimelineStore } from "../../../../store/TimelineStore";
import { createHighPPICanvas } from "../../../../utils/canvas";
import { drawCanvasTimelineRuler } from "./utils/canvasRuler";
import {useEffect, useRef, useState} from "react";
import './CanvasTimeRuler.styles.scss';
import {TIMELINE_RULER_TICK_WIDTH_PX} from "../../../../config/config";

const CanvasTimeRuler = () => {
    const [duration, setDuration] = useState(CompositionStateStore.duration);
    const [timelineZoom, setTimelineZoom] = useState(TimelineStore.zoom);
    const [baseStartSec, setBaseStartSec] = useState(TimelineStore.baseStartSec);
    const [scrollLeft, setScrollLeft] = useState(0);

    const canvasContainerRef = useRef(null);
    const canvasRef = useRef(null);
    const canvasContextRef = useRef(null);
    const canvasFillColorRef = useRef(null);

    useEffect(() => {
        const disposeAutorun = autorun(() => {
            setDuration(CompositionStateStore.duration);
            setTimelineZoom(TimelineStore.zoom);
            setBaseStartSec(TimelineStore.baseStartSec);
        });

        const disposeReaction = reaction(
            () => ApplicationStore.theme,
            updateTheme
        );

        window.addEventListener('resize', resizeCanvas);

        return () => {
            disposeAutorun();
            disposeReaction();
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);

    useEffect(() => {
        if (canvasRef.current) {
            // @ts-ignore
            canvasContextRef.current = canvasRef.current.getContext('2d');
            // @ts-ignore
            canvasFillColorRef.current = "#707070";
            resizeCanvas();
        }
    }, []);

    useEffect(() => {
        // @ts-ignore
        const handleTracksScrollLeft = (event) => {
            setScrollLeft(event.detail);
        };

        window.addEventListener('tracksScrollLeft', handleTracksScrollLeft);

        return () => {
            window.removeEventListener('tracksScrollLeft', handleTracksScrollLeft);
        };
    }, []);

    useEffect(() => {
        redrawCanvas();
    }, [duration, scrollLeft, timelineZoom, baseStartSec]);

    // amount of negative time to show for visual padding
    const LEFT_PAD_SECONDS = TimelineStore.leftPadSeconds ?? 3;

    // @ts-ignore
    const handleMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!canvasContainerRef.current) {
            return;
        }

        // @ts-ignore
        const { left: leftOffset } = canvasContainerRef.current.getBoundingClientRect();
        const x = event.clientX - leftOffset + scrollLeft;
        // apply negative-time padding so 0:00 aligns after left pad
        const leftPadPx = LEFT_PAD_SECONDS * TIMELINE_RULER_TICK_WIDTH_PX * timelineZoom;
        const xAdjusted = x - leftPadPx;

        // Emit a custom event instead of using @Event decorator
        const customEvent = new CustomEvent('seekingTimeRulerStart', {
            bubbles: true,
            detail: { event, x: xAdjusted },
        });
        // @ts-ignore
        canvasContainerRef.current.dispatchEvent(customEvent);
    };

    const updateTheme = () => {
        // @ts-ignore
        canvasFillColorRef.current = "#707070";
        redrawCanvas();
    };

    const resizeCanvas = () => {
        if (!canvasContainerRef.current || !canvasContextRef.current) {
            return;
        }

        // @ts-ignore
        const height = canvasContainerRef.current.clientHeight;
        // @ts-ignore
        const width = canvasContainerRef.current.clientWidth;

        createHighPPICanvas(canvasContextRef.current, width, height);
        redrawCanvas();
    };

    const redrawCanvas = () => {
        if (canvasContextRef.current) {
            drawCanvasTimelineRuler(canvasContextRef.current, duration, {
                scrollOffset: scrollLeft,
                fillColor: canvasFillColorRef.current ?? '#000',
                zoom: timelineZoom,
                leftPadSeconds: LEFT_PAD_SECONDS,
                baseStartSec,
            });
        }
    };

    return (
        <div
            className="canvas-time-ruler"
            onMouseDown={handleMouseDown}
            ref={canvasContainerRef}
        >
            <canvas ref={canvasRef}></canvas>
        </div>
    );
};

export default CanvasTimeRuler;
